import React from 'react';
import { Radio, Select, Button, message } from 'antd';
import type { ProductTypeId, LaneCard, ProductTypeCondition, AccommodationCondition } from '../types';

function normalizeTypeId(raw: number | string | undefined | null): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

interface ParameterSidebarProps {
  productType: ProductTypeId;
  card: LaneCard;
  options: { id: number; name: string }[];
  accommodationTypes?: { id: number; name: string }[];
  addOnTypes?: { id: number; name: string }[];
  onSave: (condition: ProductTypeCondition | AccommodationCondition) => void;
  onClear: () => void;
  onClose: () => void;
}

export const ParameterSidebar: React.FC<ParameterSidebarProps> = ({
  productType,
  card,
  options,
  accommodationTypes = [],
  addOnTypes = [],
  onSave,
  onClear,
  onClose,
}) => {
  const condition = card.condition;
  const usesTypePicker = productType === 'Accommodation' || productType === 'AddOn';
  const typeScoped = usesTypePicker ? (condition as AccommodationCondition | undefined) : undefined;

  const [localMode, setLocalMode] = React.useState<'ANY' | 'SELECT' | 'CHOOSE_TYPE'>(
    typeScoped?.mode === 'CHOOSE_TYPE' ? 'CHOOSE_TYPE' : (condition as ProductTypeCondition | undefined)?.mode ?? typeScoped?.mode ?? 'ANY'
  );
  const [localTypeId, setLocalTypeId] = React.useState<number | undefined>(() => normalizeTypeId(typeScoped?.typeId));
  const [localIds, setLocalIds] = React.useState<number[]>(condition && 'selectedIds' in condition ? condition.selectedIds : []);

  React.useEffect(() => {
    const cond = card.condition;
    const typeScopedNext = usesTypePicker ? (cond as AccommodationCondition | undefined) : undefined;
    if (typeScopedNext?.mode === 'CHOOSE_TYPE') {
      setLocalMode('CHOOSE_TYPE');
      setLocalTypeId(normalizeTypeId(typeScopedNext.typeId));
      setLocalIds([]);
      return;
    }
    if (cond && 'mode' in cond && cond.mode === 'SELECT' && 'selectedIds' in cond) {
      setLocalMode('SELECT');
      setLocalIds(cond.selectedIds ?? []);
      setLocalTypeId(undefined);
      return;
    }
    setLocalMode('ANY');
    setLocalTypeId(undefined);
    setLocalIds([]);
  }, [card.id, card.condition, usesTypePicker]);

  const typePickerOptions = productType === 'Accommodation' ? accommodationTypes : addOnTypes;

  const handleSave = () => {
    if (localMode === 'SELECT') {
      if (!localIds.length) {
        message.warning('Please select at least one product from the list.');
        return;
      }
    }
    if (localMode === 'CHOOSE_TYPE' && productType === 'Accommodation') {
      if (!localTypeId) {
        message.warning('Please choose an accommodation type.');
        return;
      }
    }
    if (localMode === 'CHOOSE_TYPE' && productType === 'AddOn') {
      if (!localTypeId) {
        message.warning('Please select a type.');
        return;
      }
    }

    if (productType === 'Accommodation') {
      if (localMode === 'ANY') {
        onSave({ mode: 'ANY', selectedIds: [] });
      } else if (localMode === 'CHOOSE_TYPE') {
        onSave({
          mode: 'CHOOSE_TYPE',
          typeId: localTypeId!,
          typeName: accommodationTypes.find((t) => Number(t.id) === localTypeId)?.name,
          selectedIds: [],
          selectedNames: [],
        });
      } else {
        const selectedNames = options.filter((o) => localIds.includes(o.id)).map((o) => o.name);
        onSave({ mode: 'SELECT', selectedIds: localIds, selectedNames });
      }
    } else if (productType === 'AddOn') {
      if (localMode === 'ANY') {
        onSave({ mode: 'ANY', selectedIds: [] });
      } else if (localMode === 'CHOOSE_TYPE') {
        onSave({
          mode: 'CHOOSE_TYPE',
          typeId: localTypeId!,
          typeName: addOnTypes.find((t) => Number(t.id) === localTypeId)?.name,
          selectedIds: [],
          selectedNames: [],
        });
      } else {
        const selectedNames = options.filter((o) => localIds.includes(o.id)).map((o) => o.name);
        onSave({ mode: 'SELECT', selectedIds: localIds, selectedNames });
      }
    } else {
      if (localMode === 'ANY') {
        onSave({ mode: 'ANY', selectedIds: [] });
      } else {
        const selectedNames = options.filter((o) => localIds.includes(o.id)).map((o) => o.name);
        onSave({ mode: 'SELECT', selectedIds: localIds, selectedNames });
      }
    }
    onClose();
  };

  const handleClear = () => {
    setLocalMode('ANY');
    setLocalTypeId(undefined);
    setLocalIds([]);
    onClear();
    onClose();
  };

  const selectLabel =
    productType === 'Course'
      ? 'Select Course'
      : productType === 'Accommodation'
        ? 'Select Accommodation'
        : productType === 'AddOn'
          ? 'Select AddOn'
          : productType === 'Fees'
            ? 'Select Fees'
            : productType === 'Package'
              ? 'Select Package'
              : 'Choose Transfer';

  const getPopupContainer = () => document.body;

  return (
    <div className="commission-parameter-sidebar">
      <div className="commission-parameter-title">{productType === 'Fees' ? 'Fees' : productType}</div>
      <div style={{ marginBottom: 12 }}>
        <Radio.Group
          value={localMode}
          onChange={(e) => setLocalMode(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Radio value="ANY">ANY</Radio>
          {usesTypePicker && <Radio value="CHOOSE_TYPE">Choose Type</Radio>}
          <Radio value="SELECT">{selectLabel}</Radio>
        </Radio.Group>
        {localMode === 'CHOOSE_TYPE' && usesTypePicker && (
          <div style={{ marginTop: 8 }}>
            <Select
              placeholder={productType === 'AddOn' ? 'Select Type' : 'Choose Type'}
              style={{ width: '100%' }}
              value={localTypeId}
              onChange={setLocalTypeId}
              options={typePickerOptions.map((t) => ({
                label: t.name,
                value: normalizeTypeId(t.id) ?? 0,
              })).filter((o) => o.value > 0)}
              getPopupContainer={getPopupContainer}
              showSearch={{
                optionFilterProp: 'label',
                filterOption: (input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.trim().toLowerCase()),
              }}
            />
          </div>
        )}
        {localMode === 'SELECT' && (
          <div style={{ marginTop: 8 }}>
            <Select
              mode="multiple"
              placeholder={productType === 'Fees' ? 'Select fees' : selectLabel}
              style={{ width: '100%' }}
              value={localIds}
              onChange={setLocalIds}
              options={options.map((o) => ({ label: o.name, value: o.id }))}
              getPopupContainer={getPopupContainer}
              showSearch={{
                optionFilterProp: 'label',
                filterOption: (input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.trim().toLowerCase()),
              }}
            />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button onClick={handleClear}>Clear</Button>
        <Button type="primary" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
};